import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Crown, ArrowUp, Grid3X3 } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import { Card } from '../../../Card';
import { Button } from '../../../Button';
import { Tooltip } from '../../../Tooltip';
import { UsernameEditor } from './UsernameEditor';
import { ThemeToggle } from '../../../ThemeToggle';
import { maskEmail } from '../utils';
import type { UserRepresent } from '../../../../../hooks/useInstitutions';

interface LoggedInViewProps {
  user: User;
  userProfile: { username: string } | null;
  isPremium: boolean;
  planType: string | null;
  premiumMode: 'FREE' | 'HIDE' | 'STANDARD';
  premiumLoading: boolean;
  isEmailVerified: boolean;
  authLoading: boolean;
  successMessage: string;

  // Username editing
  editingUsername: boolean;
  newUsername: string;
  usernameLoading: boolean;
  checkingUsername: boolean;
  usernameAvailable: boolean | null;
  onStartEditUsername: () => void;
  onSaveUsername: () => void;
  onCancelUsername: () => void;
  onUsernameChange: (value: string) => void;

  // Actions
  onLogout: () => void;
  onResendVerification: () => void;
  onClose: () => void;
  resendingVerification: boolean;

  // Institutions
  institutions: UserRepresent[];
  onToggleRepresent: (inst: UserRepresent) => void;
}

export const LoggedInView: React.FC<LoggedInViewProps> = ({
  user,
  userProfile,
  isPremium,
  planType,
  premiumMode,
  premiumLoading,
  isEmailVerified,
  authLoading,
  successMessage,
  editingUsername,
  newUsername,
  usernameLoading,
  checkingUsername,
  usernameAvailable,
  onStartEditUsername,
  onSaveUsername,
  onCancelUsername,
  onUsernameChange,
  onLogout,
  onResendVerification,
  onClose,
  resendingVerification,
  institutions,
  onToggleRepresent,
}) => {
  const navigate = useNavigate();

  return (
    <Card className="p-6">
      <div className="flex flex-col items-center space-y-4">
        {successMessage && (
          <div className="w-full p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md text-sm text-green-700 dark:text-green-300 text-center">
            {successMessage}
          </div>
        )}

        {/* User Info */}
        <div className="text-center space-y-2">
          <h3 className="text-xl font-semibold text-foreground">Welcome!</h3>
          {!isEmailVerified && (
            <Button
              variant="outline"
              size="sm"
              onClick={onResendVerification}
              disabled={resendingVerification}
              className="text-xs"
            >
              {resendingVerification ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                'Resend verification email'
              )}
            </Button>
          )}
        </div>

        {/* Email Verification Banner */}
        {!isEmailVerified && (
          <div className="w-full p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <div className="flex items-center gap-2">
              <span className="text-yellow-600 dark:text-yellow-400">⚠️</span>
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                Email not verified. Please check your inbox or resend the verification email.
              </p>
            </div>
          </div>
        )}

        {/* Username Section */}
        <div className="w-full space-y-2">
          <UsernameEditor
            username={userProfile?.username}
            isEditing={editingUsername}
            newUsername={newUsername}
            usernameLoading={usernameLoading}
            checkingUsername={checkingUsername}
            usernameAvailable={usernameAvailable}
            onStartEdit={onStartEditUsername}
            onSave={onSaveUsername}
            onCancel={onCancelUsername}
            onUsernameChange={onUsernameChange}
          />
        </div>

        {/* Email */}
        <div className="flex items-center justify-between w-full">
          <span className="text-sm font-semibold text-foreground">Email:</span>
          <span className="text-sm text-muted-foreground">
            {maskEmail(user.email)}
          </span>
        </div>

        {/* Subscription Status - Only show in STANDARD premium mode */}
        {premiumMode === 'STANDARD' && (
          <div className="flex items-center justify-between w-full">
            <span className="text-sm font-semibold text-foreground">Subscription status:</span>
            {premiumLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground">
                  {isPremium
                    ? (planType === 'lifetime_og' ? 'Premium (OG)' : planType === 'special_3m' ? 'Special Premium (3m)' : 'Premium')
                    : 'Free'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigate('/settings/subscription');
                    onClose();
                  }}
                  className="h-auto w-auto p-0 hover:bg-transparent"
                  aria-label={isPremium ? 'Manage subscription' : 'Upgrade to premium'}
                  title={isPremium ? 'Manage' : 'Upgrade'}
                >
                  {isPremium ? (
                    <Crown className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <ArrowUp className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Institution Representation Toggles */}
        {institutions.length > 0 && institutions.map(inst => {
          const representing = !inst.opt_out;
          return (
            <div key={inst.id} className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">Represent Institution</span>
                <Tooltip
                  position="top"
                  content={
                    <div className="space-y-2">
                      <p>When enabled, your institution will be shown next to your username on leaderboards.</p>
                      <div className="border-t border-border pt-2 text-xs text-muted-foreground">
                        Institution representing: <span className="font-semibold text-foreground">{inst.institution.name}</span>
                      </div>
                    </div>
                  }
                />
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={representing}
                onClick={() => onToggleRepresent(inst)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${representing ? 'bg-primary' : 'bg-muted'}`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ${representing ? 'translate-x-4' : 'translate-x-0'}`}
                />
              </button>
            </div>
          );
        })}

        {/* Theme Toggle */}
        <div className="flex items-center justify-between w-full">
          <span className="text-sm font-semibold text-foreground">Theme</span>
          <ThemeToggle />
        </div>

        {/* Progress Tracker */}
        <div className="w-full">
          <Button
            variant="outline"
            onClick={() => {
              navigate('/progress');
              onClose();
            }}
            className="w-full flex items-center justify-center gap-2"
          >
            <Grid3X3 className="h-4 w-4" />
            Progress Tracker
          </Button>
        </div>

        {/* Actions */}
        <div className="w-full space-y-3">
          <Button
            onClick={() => {
              navigate('/settings');
              onClose();
            }}
            variant="primary"
            className="w-full"
          >
            Open Settings
          </Button>
          <Button
            onClick={onLogout}
            disabled={authLoading}
            variant="outline"
            className="w-full"
          >
            {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Log out
          </Button>
        </div>
      </div>
    </Card>
  );
};
